import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Card, Empty, Segmented, Select, Space, Spin, Tag} from 'antd';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  ComplaintTaskContextResponse,
  ComplaintTaskAssigneesResponse,
  GET_COMPLAINT_TASK_CONTEXT,
  GET_COMPLAINT_TASK_ASSIGNEES,
  GET_MY_COMPLAINT_TASKS,
  MyComplaintTasksResponse,
  MyComplaintTasksVariables,
} from '../../../graphql/complaintTask/queries';
import {ComplaintTaskStage} from '../../../graphql/complaintTask/types';
import {translateGraphQLError} from '../../../utils/graphqlUtils';
import ComplaintTaskWorkPanel from './components/ComplaintTaskWorkPanel';
import TimelinessTags from './components/TimelinessTags';
import {formatTime, PRIORITY_COLOR, priorityLabel, subTypeLabel} from './complaintTaskUtils';

/**
 * QC → 客诉管理平台 → 客诉任务作业（原型 image3 / image4）。
 * 左队列（派给我的，按 未响应 / 处理中 / 已解决 切换，等级高在前、同级最新派来在前）+ 右处理区；
 * 有管理身份的人可在右上角切换「查看视角」代查他人的队列，并可直接处理（产品 10-13）。
 */
const ComplaintTaskWorkPage: React.FC = () => {
  const {t} = useTranslation();
  const [stage, setStage] = useState<ComplaintTaskStage>('UNRESPONDED');
  const [viewAs, setViewAs] = useState<number | undefined>();
  const [selected, setSelected] = useState<number | undefined>();

  const {data: ctxData} = useQuery<ComplaintTaskContextResponse>(GET_COMPLAINT_TASK_CONTEXT);
  const ctx = ctxData?.complaintTaskContext;
  // 查看视角可选的人 = 承接人列表去掉本人（层级由后端按登录人判定；仅自己时不显示切换）
  const {data: peopleData} = useQuery<ComplaintTaskAssigneesResponse>(GET_COMPLAINT_TASK_ASSIGNEES);
  const others = (peopleData?.complaintTaskAssignees ?? []).filter((p) => !p.self);
  const {data, loading, error, refetch} = useQuery<MyComplaintTasksResponse, MyComplaintTasksVariables>(
    GET_MY_COMPLAINT_TASKS, {variables: {stage, viewAsAccountId: viewAs}, fetchPolicy: 'network-only'});
  const list = useMemo(() => data?.myComplaintTasks.list ?? [], [data]);
  const serverNow = data?.myComplaintTasks.serverNow;

  // 默认选中队列第一条；当前选中项不在队列里（切阶段 / 已处理）时重新选
  useEffect(() => {
    if (!list.some((r) => r.taskId === selected)) {
      setSelected(list[0]?.taskId);
    }
  }, [list, selected]);

  return (
    <div className="flex flex-col gap-3 overflow-auto">
      <div className="flex justify-between items-center">
        <Segmented
          value={stage}
          onChange={(v) => setStage(v as ComplaintTaskStage)}
          options={(['UNRESPONDED', 'IN_PROGRESS', 'RESOLVED'] as ComplaintTaskStage[]).map((s) => ({
            value: s,
            label: t(`complaintTask.stage.${s}`),
          }))}
        />
        {ctx && others.length > 0 && (
          <Space>
            <span className="text-gray-500">{t('complaintTask.work.viewAs')}</span>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              className="w-56"
              placeholder={t('complaintTask.work.viewAsMe', {name: ctx.name})}
              value={viewAs}
              onChange={setViewAs}
              options={others.map((p) => ({value: p.accountId, label: p.name}))}
            />
          </Space>
        )}
      </div>
      {error && <Alert type="error" showIcon message={translateGraphQLError(error, 'complaintTask.errors.loadFailed')}/>}
      <div className="flex gap-4">
        <div className="w-96 shrink-0 flex flex-col gap-2">
          <div className="text-xs text-gray-400 flex justify-between">
            <span>{t('complaintTask.work.queue', {count: list.length})}</span>
            <span>{t('complaintTask.work.sortHint')}</span>
          </div>
          <Spin spinning={loading}>
            {list.length === 0 ? <Card><Empty description={t('complaintTask.work.empty')}/></Card> : list.map((r) => (
              <Card
                key={r.taskId}
                size="small"
                hoverable
                className={`mb-2 ${r.taskId === selected ? '!border-purple-500 !border-2' : ''}`}
                onClick={() => setSelected(r.taskId)}
              >
                <div className="flex justify-between items-start">
                  <Space size={4} wrap>
                    <Tag color={PRIORITY_COLOR[r.priority ?? 3]} className="!mr-0">{priorityLabel(r.priority)}</Tag>
                    <TimelinessTags row={r} serverNow={serverNow}/>
                  </Space>
                  <span className="text-xs text-gray-400">{formatTime(r.assignedAt)}</span>
                </div>
                <div className="font-medium mt-1">{r.customerName ?? '—'} · {r.title}</div>
                <div className="text-xs text-gray-400">
                  {subTypeLabel(r.subType, r.subTypeName)}{r.customerLevel ? ` · ${r.customerLevel}` : ''}
                </div>
              </Card>
            ))}
          </Spin>
        </div>
        <Card className="flex-1 min-w-0">
          {selected ? <ComplaintTaskWorkPanel taskId={selected} onChanged={() => refetch()}/> : <Empty/>}
        </Card>
      </div>
    </div>
  );
};

export default ComplaintTaskWorkPage;
